import { Box, styled, Typography, Divider, Button } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { dimensions } from '../../../../styles/dimensions';
import { theme } from '../../../../styles/theme';
import { useLocationStates } from '../store';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import NavigationIcon from '@mui/icons-material/Navigation';
import GoogleIcon from '@mui/icons-material/Google';

export const LocationInformation: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { locationInfo } = useLocationStates();

  const handleNavigate = () => {
    if (locationInfo.latitude && locationInfo.longitude) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${locationInfo.latitude},${locationInfo.longitude}`;
      window.open(url, '_blank');
    }
  };

  return (
    <ContentWrapper>
      <LeftColumn>
        <InfoSection>
          <InfoLineWrapper>
            <Label variant="body1">{t('location.name')}</Label>
            <Typography variant="body1" color="grey.100">
              {locationInfo.location}
            </Typography>
          </InfoLineWrapper>

          <InfoLineWrapper>
            <Label variant="body1">{t('location.address.label')}</Label>
            <Typography variant="body1" color="grey.100">
              {locationInfo.address}
            </Typography>
          </InfoLineWrapper>

          <InfoLineWrapper>
            <Label variant="body1">{t('location.average_emergency')}</Label>
            <Typography variant="body1" color="grey.100">
              {locationInfo.minEmergencyTeam} / {locationInfo.emergencyTeamPersons} {t('location.person')}
            </Typography>
          </InfoLineWrapper>
        </InfoSection>

        {locationInfo.sections && locationInfo.sections.length > 0 && (
          <SectionsSection>
            <SectionHeader variant="h6" color="grey.100">
              {t('location.sections.label')}
            </SectionHeader>
            <SectionsList>
              {locationInfo.sections.map(section => (
                <SectionItem key={section.id}>
                  <SectionBullet />
                  <Typography variant="body1" color="grey.100">
                    {section.name}
                  </Typography>
                </SectionItem>
              ))}
            </SectionsList>
          </SectionsSection>
        )}
      </LeftColumn>

      {locationInfo.latitude && locationInfo.longitude && (
        <MapSection>
          <MapContainer
            center={[locationInfo.latitude, locationInfo.longitude]}
            zoom={15}
            style={{ height: '100%', width: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={[locationInfo.latitude, locationInfo.longitude]} />
          </MapContainer>
          <NavigateButton
            variant="contained"
            color="primary"
            startIcon={<GoogleIcon />}
            endIcon={<NavigationIcon />}
            onClick={handleNavigate}>
            {t('location.navigate_google_maps')}
          </NavigateButton>
        </MapSection>
      )}
    </ContentWrapper>
  );
};

const { baseLG, baseMD, baseXM, base } = dimensions;

const ContentWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: ${baseLG};
  margin: ${baseMD} ${baseLG};
  width: calc(100% - 48px);
`;

const LeftColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${baseLG};
  flex: 1;
`;

const InfoSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${baseXM};
`;

const SectionsSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${baseXM};
  margin-top: ${baseMD};
`;

const SectionHeader = styled(Typography)`
  margin-bottom: ${base};
`;

const SectionsList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${base};
`;

const SectionItem = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${base};
`;

const SectionBullet = styled(Box)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${theme.palette.primary.main};
`;

const MapSection = styled(Box)`
  width: 650px;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #96c11f;
  box-shadow: 0 0 0 2px rgba(150, 193, 31, 0.1), 0 0 15px rgba(150, 193, 31, 0.2), inset 0 0 8px rgba(150, 193, 31, 0.1);
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    box-shadow: 0 0 0 2px rgba(150, 193, 31, 0.2), 0 0 20px rgba(150, 193, 31, 0.3),
      inset 0 0 12px rgba(150, 193, 31, 0.2);
  }
`;

const NavigateButton = styled(Button)`
  position: absolute;
  bottom: 16px;
  right: 16px;
  z-index: 1000;
  background-color: #4285f4; /* Google Maps blue color */
  color: white;
  font-weight: bold;
  padding: 8px 16px;
  &:hover {
    background-color: #3367d6; /* Darker Google Maps blue */
  }
`;

const InfoLineWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
`;

const Label = styled(Typography)`
  width: 150px;
  color: ${theme.palette.grey[200]};
`;
